import { LitElement, html, css } from 'lit';
import { StoreMixin, store } from '../store.js';
import { navigate, currentTool, isCurrentNav, onNavigate, NAV_BY_TOOL, TOOLS } from '../router.js';
import { api, signInUrl } from '../api.js';
import { brandLogo } from './brand-logo.js';
import { visibleNav } from '../lib/expert.js';

/**
 * The app bar: the BrandExtract logotype with the tool switcher, the primary navigation of the
 * tool the path selects, the theme control, and the account menu. Sticky, because the filter bar
 * below it is sticky too and the two read as one header.
 *
 * The eyebrow that named the tool is now the control that changes it (Estimate Tool 2.1): the
 * same 10px uppercase chartreuse treatment with a caret, opening a menu of the tools bootstrap
 * says this user may enter. It is a button with `aria-haspopup`, not a native select, because
 * the bar's other menu is already built that way and a select cannot be styled into a logotype
 * lockup across both themes. Both menus share one piece of state, so only one is ever open, and
 * one keyboard handler, so Escape closes whichever it is and hands focus back to its button.
 */
class AppNav extends StoreMixin(LitElement) {
  static properties = { _menu: { state: true } };

  static styles = css`
    /* Printing is this tool's image export (Phase 37): the navigation is for moving around the tool. */
    @media print {
      :host {
        display: none !important;
      }
    }
    :host {
      display: block;
      position: sticky;
      top: 0;
      z-index: 30;
      background: var(--bar);
      color: var(--bar-ink);
    }

    .bar {
      display: flex;
      align-items: center;
      gap: 24px;
      height: 60px;
      padding: 0 var(--page-gutter);
    }

    /* The logotype and the eyebrow were one button; the eyebrow is a control of its own now, so
       they are siblings inside the lockup. The spacing is unchanged: 12px to the rule, 12px from
       it to the word. */
    .brand {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-right: auto;
    }
    .mark {
      background: none;
      border: 0;
      padding: 0;
      color: inherit;
      cursor: pointer;
      font: inherit;
      display: block;
    }
    .mark svg {
      height: 28px;
      width: auto;
      display: block;
    }
    /* Stretched only so the menu below can hang off the full height of the bar; the rule and
       the spacing stay on the eyebrow itself, where they were, so the divider is still the
       height of the word and not the height of the bar. */
    .tools {
      position: relative;
      align-self: stretch;
      display: flex;
      align-items: center;
    }
    .tag {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--accent);
      padding-left: 12px;
      border-left: 1px solid rgba(255, 255, 255, 0.22);
      position: relative;
      top: 4px;
      white-space: nowrap;
    }
    /* The switcher wears the eyebrow's type, not a control's: it has to read as the word that
       was already there, with a caret that says it can be opened. */
    button.tag {
      display: flex;
      align-items: center;
      gap: 6px;
      background: none;
      border: 0;
      border-left: 1px solid rgba(255, 255, 255, 0.22);
      padding: 0 0 0 12px;
      font-family: var(--font-eyebrow);
      cursor: pointer;
    }
    button.tag:hover {
      color: #fff;
    }
    button.tag .caret {
      width: 7px;
      height: 7px;
      display: block;
      fill: currentColor;
      /* The caret sits on the cap height of a 10px eyebrow, not on its baseline. */
      margin-top: 1px;
    }
    button.tag[aria-expanded='true'] .caret {
      transform: rotate(180deg);
    }

    /* The primary nav scrolls rather than wraps: ten destinations never fit a laptop bar, and a
       second row would push the filter bar and the banner off the first screen. */
    nav {
      display: flex;
      gap: 2px;
      overflow-x: auto;
      scrollbar-width: none;
      margin-right: 8px;
    }
    nav::-webkit-scrollbar {
      display: none;
    }
    nav a {
      display: block;
      padding: 8px 12px;
      border-radius: var(--radius-pill);
      color: rgba(255, 255, 255, 0.78);
      text-decoration: none;
      font-size: 13.5px;
      font-weight: 600;
      white-space: nowrap;
    }
    nav a:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #fff;
    }
    nav a[aria-current='page'] {
      background: var(--accent);
      color: var(--accent-ink);
    }

    .account {
      position: relative;
      flex-shrink: 0;
    }
    .who {
      display: flex;
      align-items: center;
      gap: 8px;
      background: rgba(255, 255, 255, 0.1);
      border: 0;
      border-radius: var(--radius-pill);
      padding: 4px 12px 4px 4px;
      color: inherit;
      font: inherit;
      cursor: pointer;
    }
    .initial {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      display: grid;
      place-items: center;
      background: var(--accent);
      color: var(--accent-ink);
      font-weight: 800;
      font-size: 12px;
    }
    .name {
      font-size: 13px;
      font-weight: 500;
      max-width: 14ch;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .menu {
      position: absolute;
      right: 0;
      top: 44px;
      min-width: 232px;
      background: var(--surface);
      color: var(--ink);
      border: 1px solid var(--border);
      border-radius: 8px;
      box-shadow: var(--shadow-menu);
      overflow: hidden;
      z-index: 50;
    }
    /* The tool menu hangs off the left of the lockup; the account menu off the right of the bar. */
    .menu.under-brand {
      left: 0;
      right: auto;
      min-width: 176px;
      top: calc(100% + 8px);
    }
    /* The tool you are already in, marked by weight and not only by the tint behind it. */
    .menu [aria-current='true'] {
      background: var(--surface-2);
      font-weight: 700;
    }
    .menu .label {
      padding: 12px 14px 2px;
      font-family: var(--font-eyebrow);
      font-size: 10px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--ink-muted);
    }
    .menu .value {
      padding: 0 14px 10px;
      font-weight: 700;
    }
    .menu .rule {
      height: 1px;
      background: var(--border-soft);
    }
    .menu button,
    .menu a {
      display: block;
      width: 100%;
      text-align: left;
      padding: 10px 14px;
      background: none;
      border: 0;
      font: inherit;
      color: inherit;
      text-decoration: none;
      cursor: pointer;
    }
    .menu button:hover,
    .menu a:hover {
      background: var(--surface-2);
    }
    /* Expert mode: a checkbox row, the one control in the menu that is a setting and not a choice. */
    .menu label.check {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 14px;
      cursor: pointer;
    }
    .menu label.check:hover {
      background: var(--surface-2);
    }
    .menu label.check input {
      margin: 0;
      accent-color: var(--ink-strong);
    }
    .menu label.check .text {
      display: flex;
      flex-direction: column;
      gap: 1px;
    }
    .menu label.check small {
      font-size: 11px;
      color: var(--ink-muted);
    }
    .themes {
      display: flex;
      gap: 4px;
      padding: 8px 14px 12px;
    }
    .themes button {
      flex: 1;
      text-align: center;
      padding: 6px 4px;
      border: 1px solid var(--border);
      border-radius: 6px;
      font-size: 12px;
      background: var(--surface);
    }
    .themes button[aria-pressed='true'] {
      background: var(--ink-strong);
      color: var(--surface);
      border-color: var(--ink-strong);
    }

    @media (max-width: 900px) {
      .bar {
        height: auto;
        flex-wrap: wrap;
        padding-block: 10px;
        gap: 12px;
      }
      nav {
        order: 3;
        width: 100%;
        margin: 0;
      }
      .name {
        display: none;
      }
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    // The nav renders the tool the path selects, so it has to follow the URL and not only the
    // store: a link into the other tool changes what this bar shows.
    this._offNavigate = onNavigate(() => this.requestUpdate());
    this._onOutside = (event) => {
      if (this._menu && !event.composedPath().includes(this)) this._menu = null;
    };
    document.addEventListener('pointerdown', this._onOutside);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._offNavigate?.();
    document.removeEventListener('pointerdown', this._onOutside);
  }

  /** One menu at a time: opening either closes the other. */
  _toggle(name) {
    this._menu = this._menu === name ? null : name;
  }

  /**
   * Escape closes whichever menu is open and puts focus back on the button that opened it, so a
   * reader who never touches the pointer is not left with focus inside a menu that is gone.
   */
  _onKeydown(event) {
    if (event.key !== 'Escape' || !this._menu) return;
    const trigger = this.shadowRoot.querySelector(`[data-menu='${this._menu}']`);
    this._menu = null;
    trigger?.focus();
  }

  _go(event, path) {
    // Plain left clicks route in the browser; modified clicks stay real links so a planner can
    // open the heatmap in a second tab.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    this._menu = null;
    navigate(path);
  }

  /** Switching tools lands on that tool's first screen, carrying no filters (see router.js). */
  _switchTool(tool) {
    this._menu = null;
    navigate(tool.home ?? TOOLS[tool.id]?.home ?? '/');
  }

  async _signOut() {
    this._menu = null;
    try {
      await api.post('/auth/logout');
    } catch {
      // Already gone; the redirect below is what matters.
    }
    store.clear();
    window.location.href = signInUrl('/');
  }

  /**
   * The eyebrow. With one tool to choose from — or with bootstrap not yet answered — it stays the
   * word it has always been rather than a menu that opens onto a single entry.
   */
  _switcher(tool) {
    const tools = this.appState.tools ?? [];
    const label = tools.find((t) => t.id === tool)?.label ?? TOOLS[tool]?.label ?? 'Forecast';
    if (tools.length < 2) return html`<span class="tag">${label}</span>`;

    return html`
      <button
        class="tag"
        data-menu="tools"
        @click=${() => this._toggle('tools')}
        aria-haspopup="menu"
        aria-expanded=${this._menu === 'tools' ? 'true' : 'false'}
        aria-label="Tool: ${label}"
      >
        ${label}
        <svg class="caret" viewBox="0 0 10 10" aria-hidden="true">
          <path d="M1 3l4 4 4-4z" />
        </svg>
      </button>
      ${
        this._menu === 'tools'
          ? html`
              <div class="menu under-brand" role="menu" aria-label="Tools">
                <div class="label">Tool</div>
                ${tools.map(
                  (t) => html`
                    <button
                      role="menuitem"
                      aria-current=${t.id === tool ? 'true' : 'false'}
                      @click=${() => this._switchTool(t)}
                    >
                      ${t.label}
                    </button>
                  `,
                )}
              </div>
            `
          : ''
      }
    `;
  }

  render() {
    const { user, theme, expert } = this.appState;
    const here = window.location.pathname;
    const tool = currentTool();
    const nav = visibleNav(NAV_BY_TOOL[tool] ?? NAV_BY_TOOL.forecast, expert);
    const initial = (user?.display_name || user?.email || '?').trim().charAt(0).toUpperCase();

    return html`
      <div class="bar" @keydown=${this._onKeydown}>
        <div class="brand">
          <button class="mark" @click=${(e) => this._go(e, '/')} aria-label="WaaG home">
            ${brandLogo(28)}
          </button>
          <div class="tools">${this._switcher(tool)}</div>
        </div>

        ${
          user
            ? html`
                <nav aria-label="Primary">
                  ${nav.map(
                    (item) => html`
                      <a
                        href=${item.path}
                        aria-current=${isCurrentNav(item.path, here) ? 'page' : 'false'}
                        @click=${(e) => this._go(e, item.path)}
                        >${item.label}</a
                      >
                    `,
                  )}
                </nav>

                <div class="account">
                  <button
                    class="who"
                    data-menu="account"
                    @click=${() => this._toggle('account')}
                    aria-haspopup="menu"
                    aria-expanded=${this._menu === 'account' ? 'true' : 'false'}
                  >
                    <span class="initial">${initial}</span>
                    <span class="name">${user.display_name || user.email}</span>
                  </button>
                  ${
                    this._menu === 'account'
                      ? html`
                          <div class="menu" role="menu" aria-label="Account">
                            <div class="label">Signed in as</div>
                            <div class="value">${user.display_name || user.email}</div>
                            <div class="label">Role</div>
                            <div class="value">${user.effective_role}</div>
                            <div class="rule"></div>
                            <label class="check">
                              <input
                                type="checkbox"
                                .checked=${Boolean(expert)}
                                @change=${(e) => store.setExpert(e.target.checked)}
                              />
                              <span class="text">
                                <span>Expert mode</span>
                                <small>Every view and every filter</small>
                              </span>
                            </label>
                            <div class="rule"></div>
                            <div class="label">Theme</div>
                            <div class="themes">
                              ${['system', 'light', 'dark'].map(
                                (t) => html`
                                  <button
                                    aria-pressed=${theme === t ? 'true' : 'false'}
                                    @click=${() => store.setTheme(t)}
                                  >
                                    ${t[0].toUpperCase()}${t.slice(1)}
                                  </button>
                                `,
                              )}
                            </div>
                            <div class="rule"></div>
                            <a href="/styleguide" @click=${(e) => this._go(e, '/styleguide')}
                              >Style guide</a
                            >
                            ${
                              user.effective_role === 'admin'
                                ? html`<a href="/utils">Review utilities</a>`
                                : ''
                            }
                            <button @click=${this._signOut}>Sign out</button>
                          </div>
                        `
                      : ''
                  }
                </div>
              `
            : ''
        }
      </div>
    `;
  }
}

customElements.define('app-nav', AppNav);
